import React, { useMemo, useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { BACKGROUND_DATA, CLASS_SAVING_THROWS, SKILL_ABILITY_MAP, RACE_DATA, SPELL_SLOTS } from '../data/constants';
import { CLASS_FEATURES } from '../data/classFeatures';
import { SUBCLASS_FEATURES } from '../data/subclassFeatures';
import { RACE_FEATURES } from '../data/raceFeatures';
import { FEATS } from '../data/feats';
import { SPELLS } from '../data/spells';
import { CLASS_PROFICIENCIES, RACE_PROFICIENCIES } from '../data/proficiencies';

const initialCharacter = {
  name: 'Elara',
  class: 'Wizard',
  level: 5,
  background: 'Sage',
  race: 'Elf',
  lineage: 'High Elf',
  alignment: 'Neutral Good',
  subclass: '',
  abilityScores: {
    strength: 10,
    dexterity: 14,
    constitution: 12,
    intelligence: 18,
    wisdom: 15,
    charisma: 13,
  },
  // proficiencyBonus: 3, // Now derived
  savingThrows: {
    strength: false,
    dexterity: true,
    constitution: false,
    intelligence: true,
    wisdom: true,
    charisma: false,
  },
  skills: {
    acrobatics: false,
    animalHandling: false,
    arcana: true,
    athletics: false,
    deception: false,
    history: true,
    insight: true,
    intimidation: false,
    investigation: true,
    medicine: false,
    nature: false,
    perception: true,
    performance: false,
    persuasion: false,
    religion: false,
    sleightOfHand: false,
    stealth: false,
    survival: false,
  },
  // armorClass: 12, // Now derived
  // initiative: 2, // Now derived
  speed: 30,
  hitPoints: {
    max: 27,
    current: 27,
    temp: 0,
  },
  hitDice: '5d6',
  deathSaves: {
    successes: 0,
    failures: 0,
  },
  activeFeats: [],
  spells: {
    known: [], // For Spells Known classes (Bard, Sorcerer, Warlock, Ranger)
    prepared: [] // For Prepared classes (Cleric, Druid, Paladin, Wizard)
  },
  inventory: {
    weapons: [
      { id: 'w1', name: 'Dagger', type: 'Simple', damage: '1d4', damageType: 'Piercing', properties: ['Finesse', 'Light', 'Thrown (20/60)'], equipped: true },
      { id: 'w2', name: 'Shortbow', type: 'Simple', damage: '1d6', damageType: 'Piercing', properties: ['Ammunition', 'Two-Handed', 'Range (80/320)'], equipped: false }
    ],
    armor: [
      { id: 'a1', name: 'Leather Armor', type: 'Light', ac: 11, equipped: true },
      { id: 'a2', name: 'Shield', type: 'Shield', ac: 2, equipped: false }
    ],
    items: [
      { id: 'i1', name: 'Backpack', quantity: 1, notes: 'Holds stuff' },
      { id: 'i2', name: 'Rations', quantity: 5, notes: '1 day per ration' }
    ],
    currency: { cp: 0, sp: 0, ep: 0, gp: 10, pp: 0 }
  },
  proficiencies: {
    languages: ['Common', 'Elvish'],
    tools: [],
    weapons: ['Dagger', 'Dart', 'Sling', 'Quarterstaff', 'Light Crossbow'],
    armor: []
  },
  resources: {},
  featureChoices: {},
  featChoices: {},
  activeConditions: []
};

const getModifier = (score) => Math.floor((score - 10) / 2);



export const useCharacter = () => {
  const { token } = useAuth();
  const [character, setCharacter] = useState(initialCharacter);
  const [isRemoteLoading, setIsRemoteLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Ref to track if the current character state hasn't been saved yet
  // and to avoid saving on initial load
  const isFirstLoad = useRef(true);

  // Load Character from API
  useEffect(() => {
    if (token) {
      setIsRemoteLoading(true);
      fetch('/api/characters', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => {
          if (data && data.data) {
            // Verify it's a valid character object before setting
            setCharacter(prev => ({ ...prev, ...data.data }));
          }
        })
        .catch(err => console.error("Failed to load character", err))
        .finally(() => {
          setIsRemoteLoading(false);
          isFirstLoad.current = false;
        });
    } else {
      isFirstLoad.current = false;
    }
  }, [token]);

  // Sanitize Data Effect: Cleans up ghost entries (like undefined-Musician) from the DB
  useEffect(() => {
    if (isRemoteLoading || isFirstLoad.current) return;

    // Calculate valid keys based on CURRENT selection (simplified version of display logic)
    // We defer to a more robust check or just look for blatant errors like 'undefined-'
    const choices = character.featureChoices || {};
    const dirtyKeys = Object.keys(choices).filter(k => k.startsWith('undefined-'));

    if (dirtyKeys.length > 0) {
      console.log('Sanitizing character data:', dirtyKeys);
      setCharacter(prev => {
        const cleanChoices = { ...prev.featureChoices };
        dirtyKeys.forEach(k => delete cleanChoices[k]);
        return { ...prev, featureChoices: cleanChoices };
      });
    }
  }, [character.featureChoices, isRemoteLoading]);

  // Auto-Save Effect
  useEffect(() => {
    if (!token || isFirstLoad.current) return;

    const timer = setTimeout(() => {
      // Don't save if loading
      if (isRemoteLoading) return;

      setIsSaving(true);
      console.log('Sending save request to backend...');

      fetch('/api/characters', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          data: character
        })
      })
        .then(res => res.json())
        .then(saved => {
          console.log('Auto-saved character successfully', saved);
          setIsSaving(false);
        })
        .catch(err => {
          console.error('Auto-save failed', err);
          setIsSaving(false);
        });
    }, 1000); // 1 second debounce

    return () => clearTimeout(timer);
  }, [character, token, isRemoteLoading]);

  const toggleFeat = (featName) => {
    setCharacter(prev => {
      const active = prev.activeFeats.includes(featName)
        ? prev.activeFeats.filter(f => f !== featName)
        : [...prev.activeFeats, featName];
      return { ...prev, activeFeats: active };
    });
  };

  const toggleSpell = (spellName, type = 'prepared') => {
    setCharacter(prev => {
      const list = prev.spells[type] || [];
      const updatedList = list.includes(spellName)
        ? list.filter(s => s !== spellName)
        : [...list, spellName];

      return {
        ...prev,
        spells: {
          ...prev.spells,
          [type]: updatedList
        }
      };
    });
  };

  const inventoryActions = {
    addItem: (category, item) => {
      setCharacter(prev => ({
        ...prev,
        inventory: {
          ...prev.inventory,
          [category]: [...prev.inventory[category], item]
        }
      }));
    },
    removeItem: (category, itemId) => {
      setCharacter(prev => ({
        ...prev,
        inventory: {
          ...prev.inventory,
          [category]: prev.inventory[category].filter(i => i.id !== itemId)
        }
      }));
    },
    toggleEquip: (category, itemId) => {
      setCharacter(prev => {
        const items = prev.inventory[category].map(item => {
          if (item.id === itemId) return { ...item, equipped: !item.equipped };
          return item;
        });

        // Enforce Exclusivity for Body Armor
        if (category === 'armor') {
          const targetItem = prev.inventory.armor.find(i => i.id === itemId);
          const isEquipping = !targetItem.equipped;

          if (isEquipping && targetItem.type !== 'Shield') {
            return {
              ...prev,
              inventory: {
                ...prev.inventory,
                armor: items.map(i => {
                  if (i.id === itemId) return { ...i, equipped: true };
                  if (i.type !== 'Shield') return { ...i, equipped: false };
                  return i;
                })
              }
            };
          }
        }

        return {
          ...prev,
          inventory: {
            ...prev.inventory,
            [category]: items
          }
        };
      });
    },
    updateCurrency: (type, value) => {
      setCharacter(prev => ({
        ...prev,
        inventory: {
          ...prev.inventory,
          currency: { ...prev.inventory.currency, [type]: parseInt(value) || 0 }
        }
      }));
    }
  };

  const updateResource = (resource, value) => {
    setCharacter(prev => ({
      ...prev,
      resources: {
        ...prev.resources,
        [resource]: Math.max(0, parseInt(value) || 0)
      }
    }));
  };

  const updateFeatureChoice = (key, type, value) => {
    setCharacter(prev => ({
      ...prev,
      featureChoices: {
        ...prev.featureChoices,
        [key]: { type, value }
      }
    }));
  };

  const updateFeatChoice = (featName, type, value) => {
    setCharacter(prev => ({
      ...prev,
      featChoices: {
        ...prev.featChoices,
        [featName]: { ...prev.featChoices[featName], [type]: value }
      }
    }));
  };

  const updateProficiency = (category, item, action = 'add') => {
    setCharacter(prev => {
      const currentList = prev.proficiencies[category] || [];
      let newList;
      if (action === 'add') {
        if (currentList.includes(item)) return prev;
        newList = [...currentList, item];
      } else {
        newList = currentList.filter(i => i !== item);
      }
      return {
        ...prev,
        proficiencies: {
          ...prev.proficiencies,
          [category]: newList
        }
      };
    });
  };

  const rest = (type) => { // 'short' or 'long'
    setCharacter(prev => {
      const newResources = { ...prev.resources };
      const newHP = { ...prev.hitPoints };
      let finalConditions = prev.activeConditions || [];

      if (type === 'long') {
        newHP.current = newHP.max;

        // Clear all conditions on Long Rest
        finalConditions = [];

        // Reset All Spell Slots and Daily Resources
        Object.keys(newResources).forEach(key => {
          // Spell Slots: Delete key means 'undefined' -> defaults to MAX in Combat.js
          if (key.startsWith('spellSlot')) {
            delete newResources[key];
          }
          // Class Resources: Reset generic resources to start fresh (Max)
          if (['rage', 'ki', 'sorceryPoints', 'superiorityDice', 'channelDivinity', 'wildShape'].includes(key)) {
            delete newResources[key];
          }
        });
      }

      if (type === 'short') {
        // Recover Warlock Slots (if we had them specifically separate, but currently generic)
        // Recover Ki
        if (prev.class === 'Monk') {
          delete newResources['ki'];
        }
        if (prev.class === 'Warlock') {
          // If we tracked 'pactMagic' separate from spellSlotX, we'd reset it here.
          // For now, assume warlock slots might be handled generic or same as others. 
          // Implementation Plan note: "Recover Pact Magic & Ki".
          // Current logic: generic spellSlotX. Resetting them is trickier if mixed with multiclass.
          // MVP: Just Ki for now.
        }
      }

      return {
        ...prev,
        hitPoints: newHP,
        resources: newResources,
        activeConditions: finalConditions
      };
    });
  };

  const toggleCondition = (condition) => {
    setCharacter(prev => {
      const current = prev.activeConditions || [];
      const newConditions = current.includes(condition)
        ? current.filter(c => c !== condition)
        : [...current, condition];

      return { ...prev, activeConditions: newConditions };
    });
  };


  const updateCharacter = (path, value) => {
    setCharacter(prevCharacter => {
      // Logic to handle updates to derived values
      // If we are updating a value that is modified by a feat, we need to subtract the modifier
      // to ensure we are saving the correct 'base' value.

      let adjustedValue = value;
      const pathStr = path.join('.');

      // With derived stats, we don't need to manually subtracting feat bonuses for Init/AC/PB anymore
      // because we calculate them fresh on every render.
      // We ONLY need this if we were saving back to the "static" field, which we are removing.
      // So effectively, we just update the base value (Ability Score, Level, etc.)

      /* 
      if (prevCharacter.activeFeats.includes('Alert') && pathStr === 'initiative') {
        adjustedValue = parseInt(value) - prevCharacter.proficiencyBonus;
      }
      */

      if (prevCharacter.activeFeats.includes('Tough') && pathStr === 'hitPoints.max') {
        adjustedValue = parseInt(value) - (prevCharacter.level * 2);
      }

      const newCharacter = { ...prevCharacter };
      let current = newCharacter;
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
      }
      if (current && path[path.length - 1] === 'level') {
        adjustedValue = parseInt(adjustedValue, 10);
      }

      current[path[path.length - 1]] = adjustedValue;

      // 0.5 If Level changes, cleanup features from higher levels
      if (pathStr === 'level') {
        const newLevel = parseInt(adjustedValue, 10);
        const choiceKeys = Object.keys(newCharacter.featureChoices || {});

        choiceKeys.forEach(key => {
          const [lvlStr] = key.split('-');
          const featLevel = parseInt(lvlStr, 10);

          if (featLevel > newLevel) {
            // Cleanup active feats from ASIs/Choices
            const choice = newCharacter.featureChoices[key];
            if (choice.type === 'feat' || choice.type === 'originFeat') {
              const featName = choice.value;
              if (featName && newCharacter.featChoices?.[featName]) {
                delete newCharacter.featChoices[featName];
              }
            }
            delete newCharacter.featureChoices[key];
          }
        });
      }

      // 1. If Background changes, cleanup old background feat choices
      if (pathStr === 'background') {
        const oldBackground = prevCharacter.background;
        const oldBgFeat = BACKGROUND_DATA[oldBackground]?.feat;

        if (oldBgFeat && newCharacter.featChoices?.[oldBgFeat]) {
          delete newCharacter.featChoices[oldBgFeat];
        }
      }

      // 1. If Race changes, cleanup old race/lineage choices
      if (pathStr === 'race') {
        const oldRace = prevCharacter.race;
        const oldLineage = prevCharacter.lineage;

        newCharacter.lineage = '';

        // Find features of the OLD race and remove their choices
        const oldRaceFeats = RACE_FEATURES[oldRace] || [];
        const oldLineageFeats = oldLineage ? (RACE_FEATURES[oldLineage] || []) : [];
        const allOldFeats = [...oldRaceFeats, ...oldLineageFeats];

        allOldFeats.forEach(f => {
          // Cleanup featureChoices (Traits tab)
          const key = `${f.level}-${f.name}`;
          if (newCharacter.featureChoices[key]) {
            // If this trait granted a Feat (e.g. Versatile), we must also clean up that Feat's choices
            if (newCharacter.featureChoices[key].type === 'originFeat' || newCharacter.featureChoices[key].type === 'feat') {
              const featName = newCharacter.featureChoices[key].value;
              if (featName && newCharacter.featChoices?.[featName]) {
                delete newCharacter.featChoices[featName];
              }
            }
            delete newCharacter.featureChoices[key];
          }
        });
      }

      // 2. If Class changes, reset subclass and cleanup old class choices
      if (pathStr === 'class') {
        const oldClass = prevCharacter.class;
        newCharacter.subclass = '';

        // Clear Spells
        newCharacter.spells = { known: [], prepared: [] };

        const oldClassFeats = CLASS_FEATURES[oldClass] || [];
        // We also need to clear Subclass choices of the OLD class/subclass if any
        // But subclass is already being reset, so we just need to detect the old subclass
        const oldSubclass = prevCharacter.subclass;
        const oldSubclassFeats = oldSubclass ? (SUBCLASS_FEATURES[oldSubclass] || []) : [];

        const allOldClassFeats = [...oldClassFeats, ...oldSubclassFeats];

        allOldClassFeats.forEach(f => {
          const key = `${f.level}-${f.name}`;
          if (newCharacter.featureChoices[key]) {
            // Cleanup active feats from ASIs
            if (newCharacter.featureChoices[key].type === 'feat') {
              const featName = newCharacter.featureChoices[key].value;
              if (featName && newCharacter.featChoices?.[featName]) {
                delete newCharacter.featChoices[featName];
              }
            }
            delete newCharacter.featureChoices[key];
          }
        });
      }

      // 3. If Subclass changes, cleanup old subclass choices
      if (pathStr === 'subclass') {
        const oldSubclass = prevCharacter.subclass;
        const oldSubclassFeats = oldSubclass ? (SUBCLASS_FEATURES[oldSubclass] || []) : [];

        oldSubclassFeats.forEach(f => {
          const key = `${f.level}-${f.name}`;
          if (newCharacter.featureChoices[key]) {
            if (newCharacter.featureChoices[key].type === 'feat') {
              const featName = newCharacter.featureChoices[key].value;
              if (featName && newCharacter.featChoices?.[featName]) {
                delete newCharacter.featChoices[featName];
              }
            }
            delete newCharacter.featureChoices[key];
          }
        });
      }

      return newCharacter;
    });
  };

  // Computed Values
  const proficiencyBonus = useMemo(() => {
    return Math.ceil(character.level / 4) + 1;
  }, [character.level]);

  // Compute all active feats (manual + background)
  const computedFeats = React.useMemo(() => {
    const feats = [];

    // Validate and add manual feats
    character.activeFeats.forEach(name => {
      const featData = FEATS.find(f => f.name === name);
      if (!featData) return; // Feat not found in database

      // Check level prerequisite
      if (featData.prerequisites?.level && character.level < featData.prerequisites.level) {
        return; // Skip if level too low
      }

      // Check Epic Boon restriction
      if (featData.category === 'Epic Boon' && character.level < 19) {
        return; // Skip Epic Boons below level 19
      }

      feats.push({ name, source: 'Manual' });
    });

    const bgFeat = BACKGROUND_DATA[character.background]?.feat;
    if (bgFeat && !character.activeFeats.includes(bgFeat)) {
      feats.push({ name: bgFeat, source: `Background (${character.background})` });
    }

    // Add Feats from Feature Choices (ASI)
    Object.values(character.featureChoices || {}).forEach(choice => {
      if ((choice.type === 'feat' || choice.type === 'originFeat') && choice.value) {
        // Check for duplicates to avoid displaying twice if manually added too?
        // Ideally manual adds shouldn't happen if chosen here, but simple push is okay for now.
        feats.push({ name: choice.value, source: choice.type === 'originFeat' ? 'Origin Trait' : 'Class Feature' });
      }
    });

    return feats;
  }, [character.activeFeats, character.background, character.featureChoices, character.level]);

  // Skill Modifiers
  const skillModifiers = useMemo(() => {
    const mods = {};
    Object.keys(character.skills).forEach(skill => {
      const ability = SKILL_ABILITY_MAP[skill];
      const score = character.abilityScores[ability];
      const mod = getModifier(score);
      const isProficient = character.skills[skill];
      mods[skill] = mod + (isProficient ? proficiencyBonus : 0);
    });
    return mods;
  }, [character.skills, character.abilityScores, proficiencyBonus]);

  // Saving Throws
  const savingThrows = useMemo(() => {
    const saves = {};
    const proficientSaves = CLASS_SAVING_THROWS[character.class] || [];

    ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'].forEach(ability => {
      const score = character.abilityScores[ability];
      const mod = getModifier(score);
      const isProficient = proficientSaves.includes(ability.toLowerCase());
      saves[ability] = {
        modifier: mod + (isProficient ? proficiencyBonus : 0),
        isProficient
      };
    });
    return saves;
  }, [character.class, character.abilityScores, proficiencyBonus]);

  const displayCharacter = React.useMemo(() => {
    // Create a display version of the character with derived stats
    const c = JSON.parse(JSON.stringify(character));

    // Apply ASI Bonuses to Base Ability Scores
    const asiBonuses = { strength: 0, dexterity: 0, constitution: 0, intelligence: 0, wisdom: 0, charisma: 0 };

    // 1. From Level-based Feature Choices (ASIs)
    Object.values(character.featureChoices || {}).forEach(choice => {
      if (choice.type === 'asi' && choice.value) {
        Object.entries(choice.value).forEach(([stat, bonus]) => {
          if (asiBonuses[stat] !== undefined) asiBonuses[stat] += bonus;
        });
      }
    });

    // 2. From Feat Sub-Choices (e.g. Sentinel +1 Str)
    Object.entries(character.featChoices || {}).forEach(([featName, choice]) => {
      if (!computedFeats.some(f => f.name === featName)) return;

      if (choice.asi) {
        if (asiBonuses[choice.asi] !== undefined) asiBonuses[choice.asi] += 1;
      }
    });

    // Update c.abilityScores with the final calculated values
    Object.keys(c.abilityScores).forEach(stat => {
      c.abilityScores[stat] += (asiBonuses[stat] || 0);
    });

    c.proficiencyBonus = proficiencyBonus;
    c.skillModifiers = skillModifiers;
    c.savingThrows = savingThrows; // Overwrite storage version with computed version

    // Initiative
    const dexMod = getModifier(c.abilityScores.dexterity);
    c.initiative = dexMod;
    if (computedFeats.some(f => f.name === 'Alert')) {
      c.initiative += proficiencyBonus;
    }

    // Armor Class Calculation
    let armorAC = 10 + dexMod; // Default Unarmored

    const equippedArmor = c.inventory.armor.filter(a => a.equipped);
    const bodyArmor = equippedArmor.find(a => a.type !== 'Shield');
    const shield = equippedArmor.find(a => a.type === 'Shield');

    const hasArmor = !!bodyArmor;
    const hasShield = !!shield;

    if (bodyArmor) {
      if (bodyArmor.type === 'Light') {
        armorAC = bodyArmor.ac + dexMod;
      } else if (bodyArmor.type === 'Medium') {
        armorAC = bodyArmor.ac + Math.min(dexMod, 2);
      } else if (bodyArmor.type === 'Heavy') {
        armorAC = bodyArmor.ac; // No Dex
      }
    }

    // Class Unarmored Features (Only if no armor)
    if (!hasArmor) {
      if (c.class === 'Barbarian') {
        // Barbarian Unarmored Defense: 10 + Dex + Con. Shield allowed? Yes.
        const conMod = getModifier(c.abilityScores.constitution);
        // Use the higher of Natural (10+Dex) vs Unarmored Defense
        armorAC = Math.max(armorAC, 10 + dexMod + conMod);
      } else if (c.class === 'Monk') {
        // Monk Unarmored Defense: 10 + Dex + Wis. Shield NOT allowed.
        if (!hasShield) {
          const wisMod = getModifier(c.abilityScores.wisdom);
          armorAC = Math.max(armorAC, 10 + dexMod + wisMod);
        }
      }
      // Draconic Sorcerer? 13 + Dex. (Skip for now unless requested)
    }

    if (hasShield) {
      armorAC += 2; // Standard Shield AC
    }

    c.armorClass = armorAC;

    if (computedFeats.some(f => f.name === 'Fighting Style: Defense') && hasArmor) {
      c.armorClass += 1;
    }

    // Tough Feat HP
    if (computedFeats.some(f => f.name === 'Tough')) {
      c.hitPoints.max += c.level * 2;
    }

    // Speed Calculation
    const raceData = RACE_DATA[c.race] || { speed: 30 };
    c.speed = raceData.speed;

    // Barbarian Fast Movement (Level 5+)
    if (c.class === 'Barbarian' && c.level >= 5) {
      // Technically requires not heavy armor, assuming valid for now
      c.speed += 10;
    }

    // Monk Unarmored Movement
    if (c.class === 'Monk' && c.level >= 2) {
      let bonus = 10;
      if (c.level >= 6) bonus = 15;
      if (c.level >= 10) bonus = 20;
      if (c.level >= 14) bonus = 25;
      if (c.level >= 18) bonus = 30;
      c.speed += bonus;
    }

    // Mobile Feat (General)
    if (computedFeats.some(f => f.name === 'Mobile')) { // If it exists
      c.speed += 10;
    }

    // 3. Features & Proficiencies

    // Features List (Moved Up for calculation)
    const classFeats = (CLASS_FEATURES[c.class] || []).map(f => ({ ...f, source: 'Class' }));
    const subclassFeats = (SUBCLASS_FEATURES[c.subclass] || []).map(f => ({ ...f, source: 'Subclass' }));
    const raceFeats = (RACE_FEATURES[c.race] || []).map(f => ({ ...f, source: 'Race' }));
    const lineageFeats = c.lineage ? (RACE_FEATURES[c.lineage] || []).map(f => ({ ...f, source: 'Race' })) : [];

    // Process Feats
    const featActivities = computedFeats.map(cf => {
      const featDef = FEATS.find(f => f.name === cf.name);
      if (!featDef) return null;
      return { ...featDef, ...cf, type: featDef.actionType || 'Passive', source: 'Feat' };
    }).filter(f => f);

    const allFeatures = [
      ...classFeats.filter(f => f.level <= c.level),
      ...subclassFeats.filter(f => f.level <= c.level),
      ...raceFeats,
      ...lineageFeats,
      ...featActivities
    ];
    c.features = allFeatures;

    // Start with Class Base
    const classProfs = CLASS_PROFICIENCIES[c.class] || { armor: [], weapons: [], tools: [] };
    const raceProfs = RACE_PROFICIENCIES[c.race] || { languages: [] };
    const lineageProfs = c.lineage ? (RACE_PROFICIENCIES[c.lineage] || {}) : {};

    c.proficiencies = {
      armor: [...(classProfs.armor || [])],
      weapons: [...(classProfs.weapons || []), ...(raceProfs.weapons || []), ...(lineageProfs.weapons || [])],
      tools: [...(classProfs.tools || []), ...(raceProfs.tools || [])],
      languages: [...(raceProfs.languages || []), ...(lineageProfs.languages || [])],
      skills: []
    };

    const extraSpells = [];

    // Create a Set of valid keys based on active features to prevent stale choices from persisting
    // We explicitly EXCLUDE Feats (featActivities) because they use 'featChoices', not 'featureChoices'.
    // Including them here would validate ghost entries like 'undefined-Musician'.
    const nonFeatFeatures = [
      ...classFeats.filter(f => f.level <= c.level),
      ...subclassFeats.filter(f => f.level <= c.level),
      ...raceFeats,
      ...lineageFeats
    ];
    const validFeatureKeys = new Set(nonFeatFeatures.map(f => `${f.level}-${f.name}`));

    Object.entries(character.featureChoices || {}).forEach(([key, choice]) => {
      // Filter out assignments from features we no longer have
      if (!validFeatureKeys.has(key)) return;
      // Double check for malformed keys (e.g. undefined-Feature)
      if (key.startsWith('undefined-')) return;

      // Spells
      if (choice.type === 'cantrip' && choice.value) {
        extraSpells.push(choice.value);
      }

      // Proficiencies from choices
      const addProficiency = (targetArray, val) => {
        if (Array.isArray(val)) {
          targetArray.push(...val.filter(v => v)); // Filter empty strings
        } else if (val) {
          targetArray.push(val);
        }
      };

      if (choice.type === 'language') {
        addProficiency(c.proficiencies.languages, choice.value);
      }
      if (choice.type === 'tool' || choice.type === 'instrument') {
        addProficiency(c.proficiencies.tools, choice.value);
      }
      if (choice.type === 'skill') {
        addProficiency(c.proficiencies.skills, choice.value);
      }
    });

    // Add proficiencies from Manual Feat Choices (Feats Tab)
    const activeFeatNames = computedFeats.map(f => f.name);

    Object.entries(character.featChoices || {}).forEach(([featName, choices]) => {
      // DEBUG: console.log('Checking feat:', featName, 'Active?', activeFeatNames.includes(featName), 'Choices:', choices);
      if (!activeFeatNames.includes(featName)) return;

      const addFeatProficiency = (targetArray, val) => {
        if (Array.isArray(val)) {
          targetArray.push(...val.filter(v => v));
        } else if (val) {
          targetArray.push(val);
        }
      };

      if (choices.tool) {
        addFeatProficiency(c.proficiencies.tools, choices.tool);
      }
      if (choices.instrument) {
        addFeatProficiency(c.proficiencies.tools, choices.instrument);
      }
      if (choices.skill) {
        addFeatProficiency(c.proficiencies.skills, choices.skill);
      }
    });

    // DYNAMIC PLACEHOLDER MANAGEMENT

    // Calculate Totals Pending
    const calculatePending = (type) => {
      let totalRequired = 0;

      // From Race/Class Interactions with explicit count
      // We need to iterate all 'active' interactive features
      // c.features contains all active features from Class, Race, Lineage, Background Feats etc.
      c.features.forEach(f => {
        if (f.interactive === type) {
          totalRequired += (f.count || 1);
        }
      });

      const totalChosenFeature = Object.values(character.featureChoices || {}).filter(c => c.type === type && c.value).length;

      // Count from Feat Tab choices
      let totalChosenFeat = 0;
      c.features.forEach(f => {
        // Fix: Pending check should match the type (tool vs instrument)
        const choiceValue = character.featChoices?.[f.name]?.[type];
        if (f.interactive === type && choiceValue) {
          // If it's an array (multi-select), count valid entries
          if (Array.isArray(choiceValue)) {
            totalChosenFeat += choiceValue.filter(v => v).length;
          } else {
            totalChosenFeat++;
          }
        }
      });

      const totalChosen = totalChosenFeature + totalChosenFeat;

      // DEBUG
      if (type === 'instrument') {
        console.log(`[DEBUG] Pending Calculation for ${type}`);
        console.log('Class:', c.class);
        console.log('Features matching:', c.features.filter(f => f.interactive === type));
        console.log('Total Required:', totalRequired);
        console.log('Total Chosen:', totalChosen);
      }

      return Math.max(0, totalRequired - totalChosen);
    };

    // 1. Languages
    // Logic: If any language picked, remove 'One Extra Language' hardcoded string. 
    // And allow appending 'X Languages Pending' if we move to fully dynamic.
    // However, existing data (constants/proficiencies) hardcodes 'One Extra Language'.
    // If I switch Human/High Elf (2014) to use interactive: 'language', options: ..., count: 1.
    // Then I can use the same logic.
    // For now, let's keep the removal logic simple but robust.
    const languageChoicesCount = Object.values(character.featureChoices || {}).filter(c => c.type === 'language' && c.value).length;
    if (languageChoicesCount > 0) {
      c.proficiencies.languages = c.proficiencies.languages.filter(l => !l.includes('One Extra Language'));
    }

    // 2. Instruments (Global)
    // Clear old placeholders first to avoid duplication or conflicts. 
    // We execute this filtering ONCE before adding new placeholders.
    c.proficiencies.tools = c.proficiencies.tools.filter(t =>
      !t.includes('Musical Instrument') &&
      !t.includes('Artisan\'s Tool') &&
      !t.includes('Selection Pending')
    );

    const pendingInstruments = calculatePending('instrument');
    if (pendingInstruments > 0) {
      c.proficiencies.tools.push(`${pendingInstruments} Musical Instrument${pendingInstruments > 1 ? 's' : ''} (Selection Pending)`);
    }

    // 3. Tools (Global)
    const pendingTools = calculatePending('tool');
    if (pendingTools > 1) {
      // PendingTools logic
      c.proficiencies.tools.push(`${pendingTools} Tools (Selection Pending)`);
    } else if (pendingTools === 1) {
      c.proficiencies.tools.push(`${pendingTools} Tool (Selection Pending)`);
    }

    // Deduplicate
    c.proficiencies.armor = [...new Set(c.proficiencies.armor)];
    c.proficiencies.weapons = [...new Set(c.proficiencies.weapons)];
    c.proficiencies.tools = [...new Set(c.proficiencies.tools)];
    c.proficiencies.languages = [...new Set(c.proficiencies.languages)];
    c.proficiencies.skills = [...new Set(c.proficiencies.skills)];

    // Rebuild character.skills boolean map based on c.proficiencies.skills
    // Reset all to false first (or start fresh)
    const newSkillsMap = {};
    const ALL_SKILLS = [
      'acrobatics', 'animalHandling', 'arcana', 'athletics', 'deception', 'history', 'insight',
      'intimidation', 'investigation', 'medicine', 'nature', 'perception', 'performance',
      'persuasion', 'religion', 'sleightOfHand', 'stealth', 'survival'
    ];
    ALL_SKILLS.forEach(s => newSkillsMap[s] = false);

    c.proficiencies.skills.forEach(skillName => {
      // Convert "Animal Handling" -> "animalHandling"
      const camelCase = skillName.toLowerCase().replace(/ [a-z]/g, (g) => g[1].toUpperCase());
      if (newSkillsMap.hasOwnProperty(camelCase)) {
        newSkillsMap[camelCase] = true;
      }
    });
    c.skills = newSkillsMap;

    // Add to prepared spells (treated as known/always available)
    c.spells.prepared = [...new Set([...c.spells.prepared, ...extraSpells])];

    // Resource Limits
    c.maxResources = {
      ki: 0,
      rage: 0,
      sorceryPoints: 0,
      superiorityDice: 0
    };

    if (c.class === 'Monk') {
      c.maxResources.ki = c.level;
    }
    if (c.class === 'Barbarian') {
      // Simplified Rage table
      if (c.level < 3) c.maxResources.rage = 2;
      else if (c.level < 6) c.maxResources.rage = 3;
      else if (c.level < 12) c.maxResources.rage = 4;
      else if (c.level < 17) c.maxResources.rage = 5;
      else c.maxResources.rage = 6;
    }
    if (c.class === 'Sorcerer' && c.level >= 2) {
      c.maxResources.sorceryPoints = c.level;
    }

    // Spell Casting Stats
    // Spell Casting Stats
    const spellAttributeMap = {
      'Wizard': 'intelligence', 'Artificer': 'intelligence',
      'Cleric': 'wisdom', 'Druid': 'wisdom', 'Ranger': 'wisdom',
      'Sorcerer': 'charisma', 'Bard': 'charisma', 'Warlock': 'charisma', 'Paladin': 'charisma'
    };
    const spellAttr = spellAttributeMap[c.class];
    if (spellAttr) {
      const mod = getModifier(c.abilityScores[spellAttr]);
      c.spellSaveDC = 8 + c.proficiencyBonus + mod;
      c.spellAttackBonus = c.proficiencyBonus + mod;

      // Spell Slots
      const casterType = ['Paladin', 'Ranger'].includes(c.class) ? 'HalfCaster' : 'FullCaster';
      // Warlock Logic could be added here if we had PactMagic table
      const slots = SPELL_SLOTS[casterType]?.[c.level] || [];

      slots.forEach((count, index) => {
        if (count > 0) {
          c.maxResources[`spellSlot${index + 1}`] = count;
        }
      });

      // Warlock Pact Magic (Simplistic fallback if not in table)
      if (c.class === 'Warlock') {
        // ... Warlock logic would go here, skipping for now
      }
    }

    // Features List


    // Aggregating Combat Actions
    const combatActions = { actions: [], bonusActions: [], reactions: [], freeActions: [] };

    // 0. Pre-Aggregate Spells from Features (Static/Fixed Spells)
    allFeatures.forEach(f => {
      if (f.type === 'Spell') {
        c.spells.prepared.push(f.name);
      }
      if (f.spells && Array.isArray(f.spells)) {
        c.spells.prepared.push(...f.spells);
      }
    });

    // 1. From Features (Combat Actions)
    allFeatures.forEach(f => {
      const types = Array.isArray(f.type) ? f.type : [f.type];

      if (types.includes('Action')) combatActions.actions.push(f);
      if (types.includes('Bonus Action')) combatActions.bonusActions.push(f);
      if (types.includes('Reaction')) combatActions.reactions.push(f);
      if (types.includes('Free')) combatActions.freeActions.push(f);
    });

    // 2. From Spells
    const prepared = (c.spells?.prepared || [])
      .map(name => SPELLS.find(s => s.name === name))
      .filter(s => s);

    prepared.forEach(s => {
      if (s.castingTime.includes('Bonus Action')) combatActions.bonusActions.push({ ...s, type: 'Spell' });
      else if (s.castingTime.includes('Reaction')) combatActions.reactions.push({ ...s, type: 'Spell' });
      else if (s.castingTime.includes('Action')) combatActions.actions.push({ ...s, type: 'Spell' });
    });



    // Sort Logic: Features (Alpha) -> Spells (Level -> Alpha)
    const sortCombatItems = (items) => {
      return items.sort((a, b) => {
        const isSpellA = a.type === 'Spell';
        const isSpellB = b.type === 'Spell';

        // Features before Spells
        if (!isSpellA && isSpellB) return -1;
        if (isSpellA && !isSpellB) return 1;

        if (isSpellA && isSpellB) {
          if (a.level !== b.level) return a.level - b.level;
        }
        return a.name.localeCompare(b.name);
      });
    };

    combatActions.actions = sortCombatItems(combatActions.actions);
    combatActions.bonusActions = sortCombatItems(combatActions.bonusActions);
    combatActions.reactions = sortCombatItems(combatActions.reactions);

    c.combatActions = combatActions;

    // Granted Spells (Auto-granted from features, non-removable)
    const grantedSpells = [];

    // 1. From features with type='Spell' (e.g., Druidcraft, Dancing Lights)
    allFeatures.forEach(f => {
      if (f.type === 'Spell' && SPELLS.find(s => s.name === f.name)) {
        grantedSpells.push(f.name);
      }
    });

    // 2. From feature choices (e.g., High Elf cantrip choice)
    Object.entries(character.featureChoices || {}).forEach(([key, choice]) => {
      if (choice.type === 'cantrip' && choice.value) {
        grantedSpells.push(choice.value);
      }
    });

    c.grantedSpells = [...new Set(grantedSpells)]; // Remove duplicates

    return c;
  }, [character, proficiencyBonus, skillModifiers, savingThrows, computedFeats]);

  return { character: displayCharacter, updateCharacter, toggleFeat, toggleSpell, computedFeats, inventoryActions, updateResource, updateFeatureChoice, updateFeatChoice, updateProficiency, rest, toggleCondition, isSaving, isRemoteLoading };
};
